/**
 * Ondalık sayı ve para sözleşmesi (WP 2.10 K-4; docs/spec/castinflow/money-decimal.md).
 * - Aritmetik `decimal.js` (MIT, bakımlı; bağımlılık ağacında zaten bulunan sürüm) ile yapılır; kendi keyfi hassasiyetli sayı
 *   kütüphanemiz yoktur. Bu modül yalnız sözleşmeyi uygular: sıkı giriş biçimi, kanonik metin, politika sınırları, yuvarlama
 *   kiplerinin eşlenmesi ve tr-TR gösterimi. JavaScript `number` para/miktar ara değeri olarak KULLANILMAZ.
 * - Yalıtılmış kurucu (`Decimal.clone`, tüm ayarlar açıkça verilir): başka bir kodun genel `Decimal.set(...)` çağrısı sonuçları
 *   değiştiremez; aynı girdi her ortamda aynı sonucu verir. Toplama/çıkarma/çarpma, politika sınırları içindeki değerlerde
 *   kesindir (hassasiyet 10.000 anlamlı basamak ≫ 30 tam + 1000 ondalık basamaklık üst sınır); yuvarlama yalnız açıkça
 *   istenen yerde (`roundTo`, `decDiv`) yapılır.
 * - API ve saklama biçimi kanonik metindir: sondaki sıfırlar atılır (`"18000"`, `"12.5"`); üstel gösterim, `-0`, NaN,
 *   Infinity, baştaki `+`, baştaki sıfır ve boşluk reddedilir. Yerel ayar (tr-TR) yalnız gösterimdir (`formatDecimal`).
 * - Yuvarlama kipleri tenant ayarıyla aynı adları taşır (`half_up | half_even | down`); formül fonksiyonları için ayrıca
 *   `floor | ceil`.
 */
import { Decimal as DecimalJs } from 'decimal.js';
import { z } from 'zod';
/** Yalıtılmış kurucu: genel `DecimalJs` ayarlarından etkilenmez (her özellik açıkça verilir). */
const D = DecimalJs.clone({
    defaults: true,
    precision: 10_000,
    // Hassasiyet sınırı yalnız bölmede devreye girer; kesme (aşağı) sonrası istenen kip `decDiv`'de uygulanır.
    rounding: DecimalJs.ROUND_DOWN,
    toExpNeg: -9e15,
    toExpPos: 9e15,
    minE: -9e15,
    maxE: 9e15,
    crypto: false,
});
export const TENANT_ROUNDING_MODES = ['half_up', 'half_even', 'down'];
const RM = {
    half_up: DecimalJs.ROUND_HALF_UP, // .5 sıfırdan uzağa
    half_even: DecimalJs.ROUND_HALF_EVEN, // .5 çift komşuya
    down: DecimalJs.ROUND_DOWN, // sıfıra doğru kes
    floor: DecimalJs.ROUND_FLOOR,
    ceil: DecimalJs.ROUND_CEIL,
};
export class DecimalError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.name = 'DecimalError';
        this.code = code;
    }
}
/** Kanonik giriş biçimi: isteğe bağlı eksi, baştaki sıfırı olmayan tam kısım, isteğe bağlı kesir. */
const INPUT_RE = /^-?(0|[1-9]\d*)(\.\d+)?$/;
const MAX_INPUT_LENGTH = 64;
export const ZERO = new D('0');
/** Sıfır her zaman işaretsiz (`-0` üretilmez). */
function norm(d) {
    return d.isZero() ? ZERO : d;
}
/** Sıkı ayrıştırma (API girdisi). Hata iletileri değeri içermez. */
export function parseDecimal(input) {
    if (typeof input !== 'string' || input.length === 0 || input.length > MAX_INPUT_LENGTH)
        throw new DecimalError('format', 'Geçersiz sayı');
    if (!INPUT_RE.test(input))
        throw new DecimalError('format', 'Geçersiz sayı');
    const d = new D(input);
    if (input.startsWith('-') && d.isZero())
        throw new DecimalError('format', 'Geçersiz sayı'); // -0, -0.00
    return norm(d);
}
/** Formül sabiti gibi güvenilir rakam dizileri (baştaki sıfıra izin verir; işaret/üs yok). */
export function decimalFromDigits(raw) {
    if (!/^\d{1,60}(\.\d{1,60})?$/.test(raw))
        throw new DecimalError('format', 'Geçersiz sayı');
    return norm(new D(raw));
}
export function isDecimalString(s) {
    if (typeof s !== 'string')
        return false;
    try {
        parseDecimal(s);
        return true;
    }
    catch {
        return false;
    }
}
export function fromBigInt(n) {
    return norm(new D(n.toString()));
}
/** Güvenli tam sayıdan (metin üzerinden; kayan nokta dönüşümü yok). */
export function fromInteger(n) {
    if (!Number.isSafeInteger(n))
        throw new DecimalError('range', 'Geçersiz tam sayı');
    return norm(new D(String(n)));
}
/** Değer değişmeden kanonik temsil (işaretsiz sıfır). */
export function decNormalize(d) {
    return norm(d);
}
/** Kanonik metin (API/saklama): normal gösterim, sondaki sıfırlar yok, `-0` yok. */
export function toDecimalString(d) {
    return norm(d).toFixed();
}
/** Sabit ölçekli metin (sondaki sıfırlar korunur; gösterim/karşılaştırma testleri için). */
export function toFixedString(d, scale, mode = 'half_up') {
    const r = roundTo(d, scale, mode);
    return r.toFixed(scale);
}
/** Değeri temsil etmek için gereken ondalık basamak sayısı. */
export function scaleOf(d) {
    return d.decimalPlaces();
}
/** Tam kısmın basamak sayısı (0 → 1). */
export function intDigits(d) {
    return d.abs().trunc().toFixed().length;
}
export function decAdd(a, b) {
    return norm(a.plus(b));
}
export function decSub(a, b) {
    return norm(a.minus(b));
}
export function decMul(a, b) {
    return norm(a.times(b));
}
export function decNeg(a) {
    return norm(a.negated());
}
export function decAbs(a) {
    return norm(a.abs());
}
export function decCmp(a, b) {
    const c = a.cmp(b);
    return c < 0 ? -1 : c > 0 ? 1 : 0;
}
export function decIsZero(a) {
    return a.isZero();
}
export function decSign(a) {
    return a.isZero() ? 0 : a.isNegative() ? -1 : 1;
}
/** Verilen ölçeğe yuvarlar (ölçek 0–30; ölçek büyütme kayıpsızdır). */
export function roundTo(d, scale, mode) {
    if (!Number.isInteger(scale) || scale < 0 || scale > 30)
        throw new DecimalError('range', 'Geçersiz ölçek');
    return norm(d.toDecimalPlaces(scale, RM[mode]));
}
/**
 * a / b, sonuç `scale` ondalığa `mode` ile yuvarlanır. Bölüm önce 10.000 anlamlı basamakta kesilir, sonra istenen kiple
 * yuvarlanır. Politika sınırları içindeki işlenenlerde (≤ 1030 basamak) bu tek yuvarlamaya eşdeğerdir: p/q biçimli bir
 * bölümde bir basamak konumundan sonraki sıfır ya da dokuz dizisi q'nun basamak sayısından uzun olamaz, bu yüzden kesme
 * yuvarlama kararını değiştirmez.
 */
export function decDiv(a, b, scale, mode) {
    if (b.isZero())
        throw new DecimalError('division', 'Sıfıra bölme');
    if (!Number.isInteger(scale) || scale < 0 || scale > 60)
        throw new DecimalError('range', 'Geçersiz ölçek');
    return norm(a.div(b).toDecimalPlaces(scale, RM[mode]));
}
/** Fiyat/para: numeric(20,6) — 14 tam, 6 ondalık basamak; negatif yok. */
export const MONEY_POLICY = { maxIntDigits: 14, maxScale: 6, min: '0' };
/** Miktar: numeric(20,4) — 16 tam, 4 ondalık basamak (işaret bağlama göre). */
export const QUANTITY_POLICY = { maxIntDigits: 16, maxScale: 4 };
export const QUANTITY_SCALE = 4;
/** KDV oranı (yüzde): numeric(7,4), 0–100. */
export const VAT_RATE_POLICY = {
    maxIntDigits: 3,
    maxScale: 4,
    min: '0',
    max: '100',
};
/** Yüzde işlemi (toplu fiyat): 0 < p ≤ 1000, 4 ondalık. */
export const PERCENT_POLICY = {
    maxIntDigits: 4,
    maxScale: 4,
    min: '0',
    max: '1000',
    nonZero: true,
};
/** Politika ihlali iletisi (değer içermez) ya da `null`. */
export function policyIssue(d, p) {
    if (scaleOf(d) > p.maxScale)
        return `en çok ${String(p.maxScale)} ondalık basamak`;
    if (intDigits(d) > p.maxIntDigits)
        return 'değer çok büyük';
    if (p.min !== undefined && decCmp(d, parseDecimal(p.min)) < 0)
        return p.min === '0' ? 'negatif olamaz' : 'değer çok küçük';
    if (p.max !== undefined && decCmp(d, parseDecimal(p.max)) > 0)
        return 'değer çok büyük';
    if (p.nonZero === true && decIsZero(d))
        return 'sıfırdan farklı olmalı';
    return null;
}
/**
 * Zod: kanonik ondalık metin. Girdi sıkı biçimde ayrıştırılır, politika uygulanır, çıktı kanonik metindir.
 * `extraScale`: çağıran bağlamında daha dar ondalık sınırı (ör. ürün fiyat basamağı).
 */
export function decimalString(policy) {
    return z
        .string()
        .max(MAX_INPUT_LENGTH)
        .transform((s, ctx) => {
        let d;
        try {
            d = parseDecimal(s);
        }
        catch {
            ctx.addIssue({ code: 'custom', message: 'geçersiz sayı' });
            return z.NEVER;
        }
        const issue = policyIssue(d, policy);
        if (issue !== null) {
            ctx.addIssue({ code: 'custom', message: issue });
            return z.NEVER;
        }
        return toDecimalString(d);
    });
}
// ---------------------------------------------------------------------------
// Gösterim (yalnız arayüz/PDF; saklama biçimini değiştirmez)
// ---------------------------------------------------------------------------
/**
 * tr-TR gösterimi: binlik ayırıcı `.`, ondalık `,`. `minScale` kadar ondalık basamak doldurulur (fiyat: ürün fiyat basamağı);
 * değer daha fazla basamak taşıyorsa kesilmez (gösterim veri kaybetmez).
 */
export function formatDecimal(value, opts = {}) {
    const d = parseDecimal(value);
    const scale = Math.max(scaleOf(d), opts.minScale ?? 0);
    const fixed = toFixedString(d, scale);
    const neg = fixed.startsWith('-');
    const body = neg ? fixed.slice(1) : fixed;
    const [i = '0', f] = body.split('.');
    const grouped = i.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${neg ? '\u2212' : ''}${grouped}${f !== undefined ? `,${f}` : ''}`;
}
/**
 * Kullanıcı girdisini (tr-TR veya nokta ondalıklı) kanonik metne çevirir; çevrilemezse `null`. Kurallar: boşluklar atılır;
 * hem `.` hem `,` varsa `.` binlik, `,` ondalıktır; yalnız `,` ondalıktır; yalnız `.` varsa `1.000` / `12.345.678` gibi tam üçlü
 * gruplar binlik, aksi hâlde ondalık noktasıdır. Sonuç yine sıkı ayrıştırmadan geçer (üstel gösterim yok).
 */
export function parseDecimalInput(raw) {
    let s = raw.replace(/[\s\u00a0\u202f]/g, '').replace(/^\u2212/, '-');
    if (s === '')
        return null;
    const hasDot = s.includes('.');
    const hasComma = s.includes(',');
    if (hasDot && hasComma) {
        if (s.lastIndexOf(',') < s.lastIndexOf('.'))
            return null;
        s = s.replace(/\./g, '').replace(',', '.');
    }
    else if (hasComma) {
        if ((s.match(/,/g) ?? []).length > 1)
            return null;
        s = s.replace(',', '.');
    }
    else if (hasDot && /^-?\d{1,3}(\.\d{3})+$/.test(s)) {
        s = s.replace(/\./g, '');
    }
    // Baştaki sıfırlar (ör. "007") kullanıcı girdisinde kabul edilir, kanonik biçime indirgenir
    s = s.replace(/^(-?)0+(?=\d)/, '$1');
    try {
        return toDecimalString(parseDecimal(s));
    }
    catch {
        return null;
    }
}
// ---------------------------------------------------------------------------
// ISO 4217 para birimleri (K-4)
// ---------------------------------------------------------------------------
/**
 * ISO 4217 etkin para birimleri (fon kodları BOV/CHE/CHW/CLF/COU/MXV/USN/UYI/UYW, kıymetli maden XAG/XAU/XPD/XPT, test ve
 * birim kodları XBA–XBD/XDR/XSU/XTS/XUA/XXX hariç; bölgesel XAF/XCD/XCG/XOF/XPF dâhil). Yakın zamanda geçiş yapan kodlar
 * (ör. ANG → XCG, BGN, SLL → SLE, ZWL → ZWG) mevcut kayıtlar okunabilsin diye listede tutulur.
 */
export const CURRENCY_CODES = [
    'AED',
    'AFN',
    'ALL',
    'AMD',
    'ANG',
    'AOA',
    'ARS',
    'AUD',
    'AWG',
    'AZN',
    'BAM',
    'BBD',
    'BDT',
    'BGN',
    'BHD',
    'BIF',
    'BMD',
    'BND',
    'BOB',
    'BRL',
    'BSD',
    'BTN',
    'BWP',
    'BYN',
    'BZD',
    'CAD',
    'CDF',
    'CHF',
    'CLP',
    'CNY',
    'COP',
    'CRC',
    'CUP',
    'CVE',
    'CZK',
    'DJF',
    'DKK',
    'DOP',
    'DZD',
    'EGP',
    'ERN',
    'ETB',
    'EUR',
    'FJD',
    'FKP',
    'GBP',
    'GEL',
    'GHS',
    'GIP',
    'GMD',
    'GNF',
    'GTQ',
    'GYD',
    'HKD',
    'HNL',
    'HTG',
    'HUF',
    'IDR',
    'ILS',
    'INR',
    'IQD',
    'IRR',
    'ISK',
    'JMD',
    'JOD',
    'JPY',
    'KES',
    'KGS',
    'KHR',
    'KMF',
    'KPW',
    'KRW',
    'KWD',
    'KYD',
    'KZT',
    'LAK',
    'LBP',
    'LKR',
    'LRD',
    'LSL',
    'LYD',
    'MAD',
    'MDL',
    'MGA',
    'MKD',
    'MMK',
    'MNT',
    'MOP',
    'MRU',
    'MUR',
    'MVR',
    'MWK',
    'MXN',
    'MYR',
    'MZN',
    'NAD',
    'NGN',
    'NIO',
    'NOK',
    'NPR',
    'NZD',
    'OMR',
    'PAB',
    'PEN',
    'PGK',
    'PHP',
    'PKR',
    'PLN',
    'PYG',
    'QAR',
    'RON',
    'RSD',
    'RUB',
    'RWF',
    'SAR',
    'SBD',
    'SCR',
    'SDG',
    'SEK',
    'SGD',
    'SHP',
    'SLE',
    'SLL',
    'SOS',
    'SRD',
    'SSP',
    'STN',
    'SVC',
    'SYP',
    'SZL',
    'THB',
    'TJS',
    'TMT',
    'TND',
    'TOP',
    'TRY',
    'TTD',
    'TWD',
    'TZS',
    'UAH',
    'UGX',
    'USD',
    'UYU',
    'UZS',
    'VED',
    'VES',
    'VND',
    'VUV',
    'WST',
    'XAF',
    'XCD',
    'XCG',
    'XOF',
    'XPF',
    'YER',
    'ZAR',
    'ZMW',
    'ZWG',
    'ZWL',
];
const CURRENCY_SET = new Set(CURRENCY_CODES);
export function isCurrencyCode(s) {
    return typeof s === 'string' && CURRENCY_SET.has(s);
}
/** Zod: ISO 4217 kodu (üç büyük harf, listede). Küçük harf girdi reddedilir (sözleşme büyük harftir). */
export const currencySchema = z
    .string()
    .regex(/^[A-Z]{3}$/, 'para birimi üç büyük harfli ISO 4217 kodu olmalı')
    .refine((s) => CURRENCY_SET.has(s), { message: 'bilinmeyen para birimi' });
//# sourceMappingURL=decimal.js.map